import { useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useId, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { deleteMyAccount, signOut } from "./api";
import { LoginScreen } from "./login";

/**
 * La suppression du compte (ticket 8, #515), pour le proprietaire (« Supprimer ma frise et mon
 * compte », dans les reglages) comme pour un proche (« Supprimer mon accès », sous sa frise).
 *
 * Le bouton ouvre une fenetre qui dit ce qui part et ce qui reste. Le proprietaire confirme en
 * tapant un mot (`confirmWord`) : sa frise ne revient pas. Un proche confirme par un second bouton.
 * Puis : `DELETE /api/t/compte`, `DELETE /api/me` (le compte), la deconnexion, et l'ecran de
 * connexion avec un mot qui dit que c'est fait. Le cache des requetes privees est vide : rien de
 * la frise ne reste dans la page.
 */
export function DeleteAccount({
  buttonLabel,
  heading,
  explanation,
  confirmWord,
  confirmLabel,
  doneMessage,
}: {
  buttonLabel: string;
  heading: string;
  explanation: string;
  confirmWord?: string;
  confirmLabel: string;
  doneMessage: string;
}) {
  const queryClient = useQueryClient();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);

  // A l'ouverture, le focus va au champ (ou a « Annuler ») ; a la fermeture, il revient au bouton.
  useEffect(() => {
    if (open) (field.current ?? cancel.current)?.focus();
  }, [open]);

  const close = () => {
    if (busy) return;
    setOpen(false);
    setTyped("");
    setFailure(null);
    opener.current?.focus();
  };

  const confirmed =
    confirmWord === undefined || typed.trim().toLowerCase() === confirmWord.toLowerCase();

  async function erase(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!confirmed || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await deleteMyAccount();
    } catch (error) {
      setBusy(false);
      setFailure(
        error instanceof ApiError
          ? `La suppression n'a pas abouti : ${error.message}`
          : "La suppression n'a pas abouti. Réessaie dans un instant.",
      );
      return;
    }
    // Le compte n'existe plus : la session non plus. La deconnexion efface le cookie, sans plus.
    await signOut().catch(() => undefined);
    queryClient.removeQueries({ queryKey: ["t"] });
    setDone(true);
  }

  if (done) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-background px-4">
        <p className="mx-auto mt-10 max-w-md text-base text-tl-ok" role="status">
          {doneMessage}
        </p>
        <LoginScreen />
      </div>
    );
  }

  return (
    <>
      <button
        className="min-h-11 rounded-[10px] border border-destructive bg-transparent px-4 text-sm text-destructive"
        onClick={() => setOpen(true)}
        ref={opener}
        type="button"
      >
        {buttonLabel}
      </button>
      {open ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-tl-veil px-4">
          <div
            aria-describedby={`${id}-texte`}
            aria-labelledby={`${id}-titre`}
            aria-modal="true"
            className="w-full max-w-md rounded-xl border border-border bg-card p-5 text-foreground"
            onKeyDown={(event) => {
              if (event.key === "Escape") close();
            }}
            role="dialog"
          >
            <form className="flex flex-col gap-3" onSubmit={(event) => void erase(event)}>
              <h2 className="m-0 text-[17px] font-semibold" id={`${id}-titre`}>
                {heading}
              </h2>
              <p className="m-0 text-sm text-muted-foreground" id={`${id}-texte`}>
                {explanation}
              </p>
              {confirmWord === undefined ? null : (
                <label className="flex flex-col gap-1 text-sm">
                  <span>
                    Pour confirmer, tape <strong>{confirmWord}</strong>
                  </span>
                  <input
                    autoComplete="off"
                    className="min-h-11 rounded-[10px] border border-input bg-tl-field px-3 text-base text-foreground"
                    onChange={(event) => setTyped(event.target.value)}
                    ref={field}
                    value={typed}
                  />
                </label>
              )}
              {failure === null ? null : (
                <p className="m-0 text-sm text-destructive" role="alert">
                  {failure}
                </p>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  className="min-h-11 rounded-[10px] border border-input px-4 text-sm"
                  disabled={busy}
                  onClick={close}
                  ref={cancel}
                  type="button"
                >
                  Annuler
                </button>
                <button
                  className="min-h-11 rounded-[10px] bg-destructive px-4 text-sm font-semibold text-background disabled:opacity-60"
                  disabled={!confirmed || busy}
                  type="submit"
                >
                  {busy ? "Suppression..." : confirmLabel}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}
